import { useState, useCallback, Fragment } from 'react';

import { useForm, RegisterOptions, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MultiSelectField } from '@/components/atoms/MultiSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCloseModal } from '@/hooks/useCloseModal';
import { submitWithTrimming } from '@/util/util';
import { errorMessage } from '@/util/validation/helpers';

type Props = {
  onCreate: (data) => void;
  resource: string;
  title: string;
  size?: 'small' | 'medium' | 'large';
  primaryButtonText?: string;
  mode?: 'onChange' | 'onSubmit';
  fields?: {
    fieldName: string;
    label: string;
    fieldValue: string;
    readOnly?: boolean;
    rules?: Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
    type?: 'input' | 'select' | 'autocomplete';
    options?: { label: string; value: string; description?: string }[];
    placeholder?: string;
  }[];
  critical?: React.ReactNode;
  banner?: React.ReactNode;
  preventClose?: boolean;
};

const ModalForm = ({
  onCreate,
  resource,
  title,
  fields = [],
  critical,
  size,
  mode = 'onChange',
  banner = null,
  primaryButtonText,
  preventClose,
}: Props) => {
  const { t } = useTranslation();
  const closeModal = useCloseModal();
  const [isLoading, setIsLoading] = useState(false);
  const formMethods = useForm({
    mode,
    defaultValues: fields.reduce(
      (acc, e) => {
        return { ...acc, [e.fieldName]: e.fieldValue };
      },
      {} as Record<string, string>
    ),
  });

  const {
    formState: { errors },
    control,
  } = formMethods;

  const onSubmit = useCallback(
    async (data) => {
      setIsLoading(true);
      try {
        // Transform autocomplete fields from string[] back to OptionBase[]
        const transformedData = { ...data };
        fields.forEach((field) => {
          if (field.type === 'autocomplete' && Array.isArray(data[field.fieldName])) {
            transformedData[field.fieldName] = data[field.fieldName]
              .map((value: string) => field.options?.find((opt) => opt.value === value))
              .filter(Boolean);
          }
        });
        await onCreate(transformedData);
      } catch (error) {
        console.error(error);
      }
    },
    [onCreate, fields]
  );

  const sizeClass = size === 'large' ? 'sm:max-w-5xl' : size === 'medium' ? 'sm:max-w-2xl' : '';

  return (
    <Dialog open onOpenChange={(open) => !open && !preventClose && closeModal()}>
      <DialogContent
        className={sizeClass}
        showCloseButton={!preventClose}
        onPointerDownOutside={(event) => {
          if (preventClose) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (preventClose) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title ?? t('modals.create', { 0: resource })}</DialogTitle>
        </DialogHeader>
        <Form {...formMethods}>
          <div>
            {fields.map(
              (
                {
                  fieldName,
                  fieldValue,
                  readOnly = false,
                  rules,
                  type = 'input',
                  options = [],
                  placeholder = '',
                  label = '',
                },
                i
              ) => (
                <Fragment key={fieldName}>
                  {type === 'input' && (
                    <FormField
                      control={control}
                      name={fieldName}
                      rules={rules}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{label || fieldName.replace('_', ' ')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              readOnly={readOnly}
                              placeholder={fieldValue}
                              autoFocus={i === 0}
                            />
                          </FormControl>
                          <FormMessage>
                            {errorMessage({ field: errors[fieldName], ...rules })}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  )}

                  {type === 'select' && (
                    <FormField
                      control={control}
                      name={fieldName}
                      rules={rules}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{label || fieldName.replace('_', ' ')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={placeholder} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="z-1400">
                              {options.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  {type === 'autocomplete' && (
                    <Controller
                      control={control}
                      name={fieldName}
                      rules={rules}
                      render={({ field }) => (
                        <MultiSelectField
                          title={label || fieldName}
                          options={
                            options?.map((opt) => ({
                              label: opt.label,
                              value: opt.value,
                              description: opt.description,
                            })) ?? []
                          }
                          placeholder={placeholder}
                          error={!!errors?.[fieldName]?.message}
                          value={field.value ?? []}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  )}
                </Fragment>
              )
            )}
          </div>
          {critical && <div>{critical}</div>}
          {banner && <div>{banner}</div>}
        </Form>
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submitWithTrimming(formMethods, onSubmit)} isLoading={isLoading}>
            {primaryButtonText ?? t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalForm;
